import {cloneDeep} from 'lodash';
import {DisplayUtils} from '../helpers';
import {FactoryService, WebService} from '../services';
import {
	ContainerTransitionMappingStore,
	ENGAGEMENT_SOURCE,
	IBundleGroup,
	IBundleGroupOption,
	IEngagementSource,
	IFeaturedItem,
	IItemFavorite,
	ILineItem,
	ILineItemOption,
	ILineItemOptionChoice,
	IMenuItem,
	IMinMaxChoicesSet,
	IRecentItem,
	ITrendingItemWithOptions,
	TEngagementSourceLocation,
	MenuStore,
	FeaturedItemStore,
	TEngagementSource,
	ITrendingItem
} from '../store';
import type {TItemIdentifier} from '../types';

interface ILineItemValidateOptionsParams {
	forceMerge?: boolean;
}

export type TMinMaxStatus = Pick<IMinMaxChoicesSet, 'max' | 'min' | 'name'> & {
	count: number;
};

type TOptionId = ILineItemOption['optionId'];


export class ItemValidationError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'ItemValidationError';
	}
}

class Item {
	//
	//
	// MENU ITEM METHODS
	//

	/**
	 * returns the menu item's contained item, if present, or its line item
	 *
	 * If hasContainer is false, the menu item's direct line item is returned.
	 *
	 * @param menuItem
	 * @param containerChoiceId
	 */
	menuItemGetLineItem(menuItem: IMenuItem | null, containerChoiceId?: ILineItemOptionChoice['choiceId']): ILineItem | null {
		if(menuItem?.lineItem) {
			const selectedChoice = this.lineItemGetContainerChoice(menuItem.lineItem, containerChoiceId);

			return selectedChoice?.lineItem ?? menuItem.lineItem;
		}

		return null;
	}

	/**
	 * validates that the givenOptions' choices exist on menuItem
	 *
	 * If the given menuItem is a container item, this method identifies the contained
	 * item based on givenOptions. The contained or non-container line item is
	 * then passed to lineItemValidateOptions.
	 *
	 * @param menuItem
	 * @param givenOptions
	 * @return ILineItem (contained) line item updated with givenOptions
	 * @throws ItemValidationError
	 */
	menuItemValidateOptions(menuItem: IMenuItem, givenOptions: ILineItemOption[]): ILineItem {
		if(!menuItem || !Array.isArray(givenOptions)) {
			throw new ItemValidationError('Invalid parameter(s).');
		}

		if(menuItem.lineItem && Array.isArray(menuItem.lineItem.options)) {
			/*
			 * Find the lineItem to validate. If the menu item is a container item, use
			 * the contained item. Otherwise, use the menu item's line item, directly.
			 */
			if(menuItem.lineItem.hasContainer) {
				// find the option ID for the container option
				const containerOption = menuItem.lineItem.options.find(opt => opt.isContainerOption);
				if(!containerOption) {
					throw new ItemValidationError('Unable to find container option.');
				}

				const containerOptionId = containerOption.optionId;
				// from the given options, find the one matching the container
				const customerContainerOption = givenOptions.find(opt => opt.optionId === containerOptionId);

				if(customerContainerOption) {
					// find the customer's container choice selection
					const customerSelectedContainerOptionChoice = customerContainerOption.choices.find(chc => this.choiceIsUserSet(chc));
					if(customerSelectedContainerOptionChoice) {
						// from the selected choice, find the selected option in the containing item
						const selectedContainerChoice = containerOption.choices.find(
							chc => chc.choiceId === customerSelectedContainerOptionChoice.choiceId
						);
						if(selectedContainerChoice && selectedContainerChoice.lineItem) {
							return this.lineItemValidateOptions(selectedContainerChoice.lineItem, givenOptions);
						}
					}
				}
			} else {
				return this.lineItemValidateOptions(menuItem.lineItem, givenOptions);
			}
		} else {
			throw new ItemValidationError('Invalid parameter(s).');
		}

		throw new ItemValidationError('Line item not found.');
	}


	//
	//
	// BUNDLE/CONTAINER METHODS
	// N.B.: When a method could apply to an ILineItem or its bundle groups, "item"
	//       is the method prefix, not "lineItem" or "bundleGroup" used elsewhere.
	//

	/**
	 * returns the single, selected option in a bundle group, or null if none selected
	 *
	 * @param group
	 */
	bundleGroupGetSelectedOption(group: IBundleGroup): IBundleGroupOption | null {
		for(const option of group.options) {
			if(option.selected && option.lineItem) {
				return option;
			}
		}
		return null;
	}

	/**
	 * validates that the bundle group's selected option's line item meets all its internal requirements
	 *
	 * @param group
	 */
	bundleGroupIsValid(group: IBundleGroup): boolean {
		const selectedOption = this.bundleGroupGetSelectedOption(group);
		return selectedOption !== null &&
			this.lineItemIsValid(selectedOption.lineItem as ILineItem);
	}

	/**
	 * validates a line item or line item's bundle groups
	 *
	 * @param lineItem
	 */
	itemIsValid(lineItem: ILineItem): boolean {
		if(lineItem.type === 'item') {
			return this.lineItemIsValid(lineItem);
		} else if(lineItem.groups) {
			let bundleIsValid = true;
			for(const group of lineItem.groups) {
				if(!this.bundleGroupIsValid(group)) {
					bundleIsValid = false;
					break;
				}
			}
			return bundleIsValid;
		}

		return false;
	}


	//
	//
	// LINE ITEM METHODS
	//

	/**
	 * get a full line item configured with the given options, if possible
	 *
	 * If the given options can not be applied to the line item, a null value
	 * is returned. This method uses the ContainerTransitionMappingStore to
	 * translate container items to/from identical non-container items, where
	 * necessary (e.g., when migrating an order to a new location).
	 */
	lineItemGetById(identifiers: TItemIdentifier): ILineItem | null {
		let {
			itemId = null,
			itemHqId = null,
			itemOptions = [],
		} = identifiers;

		let optionsCopy = cloneDeep(itemOptions);
		let menuItem = null;

		// Is there a container item that matches the given itemHqId?
		const upgradeMapping = ContainerTransitionMappingStore.upgradeMapping(itemHqId);
		// CPH - TODO: This will miss the case for passing in item b where item a has an upgrade mapping to item b and b has an upgrade mapping to item c, but both b and c are removed from the menu
		// I don't know if that's a big deal or not
		if(upgradeMapping) {
			const upgradeItem = MenuStore.itemByHqId(upgradeMapping.itemHqId);
			if(upgradeItem) {
				itemId = upgradeItem.itemId;
				itemHqId = upgradeItem.itemHqId;

				const newLineItemOption = FactoryService.lineItemOption();
				newLineItemOption.optionId = upgradeMapping.optionId;
				newLineItemOption.choices[0].choiceId = upgradeMapping.choiceId;
				newLineItemOption.choices[0].isUserSet = true;
				newLineItemOption.choices[0].selected = true;
				optionsCopy.push(newLineItemOption);
			}
		} else {
			menuItem = MenuStore.item(itemId, itemHqId);
			if(!menuItem) {
				const downgradeMappings = ContainerTransitionMappingStore.downgradeMappings(itemHqId);
				if(downgradeMappings !== null) {
					downgradeMappings.every(
						(downgradeMapping) => {
							const downgradeItem = MenuStore.itemByHqId(downgradeMapping.itemHqId);
							if(downgradeItem) {
								const matchingOption = optionsCopy.find(
									(opt) => {
										return opt.optionId === downgradeMapping.optionId;
									}
								);

								if(matchingOption) {
									const matchingChoice = matchingOption.choices.find(
										(choice) => {
											return choice.choiceId === downgradeMapping.choiceId && choice.selected;
										}
									);
									if(matchingChoice) {
										optionsCopy = optionsCopy.filter(
											(o) => {
												return o.optionId !== downgradeMapping.optionId;
											}
										);
										menuItem = downgradeItem;
										return false;
									}
								}
							}
						}
					);
				}
			}
		}

		// We didn't find an upgrade/downgrade
		if(menuItem === null) {
			menuItem = MenuStore.item(itemId, itemHqId);
		}

		if(menuItem === null) {
			console.error(`No menu item found for item (id: ${itemId}, hqId: ${itemHqId}).`);
			throw new Error('Menu item not found.');
		}

		try {
			return this.menuItemValidateOptions(menuItem, optionsCopy);
		} catch(e) {
			if(e instanceof ItemValidationError) {
				console.warn('Item validation failed.', e.message);
				return null;
			}
			throw e;
		}
	}

	/**
	 * returns full path to image to use for item
	 *
	 * If this is a contained item, the image to use should come from `containerImage`,
	 * if set. For menu items, the container image comes from the container option's
	 * choice. Look there, if provided, if the contained item does not have the value.
	 * If all else fails, return the value of `mobileImage`.
	 *
	 * All values are passed through WebService.assetPath().
	 *
	 * @param lineItem
	 * @param containerChoice ILineItemOptionChoice
	 */
	lineItemGetImagePath(lineItem: ILineItem | null, containerChoice?: ILineItemOptionChoice | null): string {
		if(lineItem === null) {
			return '';
		}

		let featuredItem: IFeaturedItem | null = null;
		if(this.engagementSourceIsFeatured(lineItem.engagementSource)) {
			featuredItem = FeaturedItemStore.featuredItem(lineItem.engagementSource.id);
		}

		if(featuredItem !== null && featuredItem.image !== null) {
			return WebService.assetPath(featuredItem.image) ?? '';
		} else {
			if(lineItem.hasContainer) {
				if(lineItem.containerImage) {
					return WebService.assetPath(lineItem.containerImage) ?? '';
				} else if(containerChoice) {
					return WebService.assetPath(containerChoice.containerImage) ?? '';
				}
			}
		}

		return WebService.assetPath(lineItem?.mobileImage) ?? '';
	}

	/**
	 * validates and returns the given containerChoiceId from lineItem
	 *
	 * If the given containerChoiceId is missing or not found, this method returns
	 * the first container choice for an item that hasContainer. If all this fails
	 * to find a choice for a contained item (i.e., the option has none), an error
	 * is thrown.
	 *
	 * If hasContainer is false, null is returned.
	 *
	 * In contrast to lineItemGetSelectedContainerChoice, this method looks for the
	 * given container choice and returns it. This looks for a specific choice.
	 *
	 * @param lineItem
	 * @param containerChoiceId
	 */
	lineItemGetContainerChoice(lineItem: ILineItem, containerChoiceId?: ILineItemOptionChoice['choiceId']): ILineItemOptionChoice | null {
		if(lineItem?.hasContainer) {
			const containerOption = lineItem.options.find(opt => opt.isContainerOption);
			if(!containerOption) {
				console.error(`Unable to find container option for ${lineItem.name}.`);
				return null;
			}
			const choices = containerOption.choices;

			if(Array.isArray(choices)) {
				let selectedChoice: ILineItemOptionChoice | undefined;

				if(typeof containerChoiceId !== 'undefined') {
					selectedChoice = choices.find(
						choice => choice.choiceId === containerChoiceId
					);
				}

				/*
				 * If we have a contained item without a specified container option
				 * choice, or if we were given a containerChoiceId we couldn't find,
				 * default to the first choice in the option.
				 */
				if(typeof containerChoiceId === 'undefined' ||
					typeof selectedChoice === 'undefined'
				) {
					selectedChoice = choices?.[0];
				}

				/*
				 * Throw an error if
				 *  - we were given a containerChoiceId and couldn't find it, and/or
				 *  - we tried to default to the first choice, and
				 *  - there were no choices
				 */
				if(typeof selectedChoice === 'undefined') {
					throw new Error('Unable to find containing item choice.');
				}

				return selectedChoice;
			}
		}

		return null;
	}

	/**
	 * returns array of options found in line item by their IDs
	 *
	 * @param lineItem
	 * @param optionIds
	 */
	lineItemGetOptionsById(lineItem: ILineItem, optionIds: Array<TOptionId>): ILineItemOption[] {
		return optionIds.map(optId => {
			const option = lineItem.options.find((o) => {
				return o.optionId === optId;
			});
			if(!option) {
				// This could be fine, if you have an option on the free choices set that
				// isn't on your item (directly or via the grid).
				console.warn(`No option found for option ${optId}`);
				return null;
			}
			return option;
		}).filter(opt => opt !== null) as ILineItemOption[];
	}

	/**
	 * indicates whether line item has given option in a min/max choice set
	 *
	 * @deprecated use optionIsInMinMax
	 *
	 * @param lineItem
	 * @param optionId
	 */
	lineItemHasMinMaxOption(lineItem: ILineItem, optionId: TOptionId): boolean {
		return this.optionIsInMinMax(optionId, lineItem);
	}

	/**
	 * validates that the line item meets all its internal requirements
	 *
	 * In order for the line item to be valid, all of its options must also
	 * be valid. This method uses `optionIsValid`.
	 *
	 */
	lineItemIsValid(lineItem: ILineItem | null): boolean {
		if(lineItem === null) {
			return false;
		}

		for(const option of lineItem.options) {
			const optionIsValid = this.optionIsValid(option);
			if(!optionIsValid) {
				return false;
			}
		}

		/*
		 * Yes, we run the same loop again, here. We want to avoid doing
		 * these checks unless we have to, because they are expensive.
		 * This, second loop only runs if the options are all valid, above.
		 */
		for(const option of lineItem.options) {
			if(!this.lineItemMinMaxIsValidForOption(lineItem, option.optionId)) {
				return false;
			}
		}

		return true;
	}

	/**
	 * indicates whether line item's min/max choice set is valid for the given option
	 *
	 * If the option is not part of a min/max choice set, the method returns true.
	 *
	 * @param lineItem
	 * @param optionId
	 * @param requirement indicates which boundary/ies to check: min, max or both
	 */
	lineItemMinMaxIsValidForOption(lineItem: ILineItem, optionId: TOptionId, requirement: 'min' | 'max' | 'both' = 'both'): boolean {
		const minMax = this.lineItemMinMaxStatusForOption(lineItem, optionId);

		if(minMax === null) {
			return true;
		}

		switch(requirement) {
			case 'min':
				return minMax.min <= minMax.count;
			case 'max':
				return minMax.count <= minMax.max;
			case 'both':
			default:
				return minMax.min <= minMax.count && minMax.count <= minMax.max;
		}
	}

	/**
	 * returns the total choices selected in the min/max choice set including the given option
	 *
	 * If the option is not part of a min/max choice set, the method returns null. Otherwise,
	 * it returns a TMinMaxStatus object containing the current total, maximum and minimum.
	 *
	 * @param lineItem
	 * @param optionId
	 */
	lineItemMinMaxStatusForOption(lineItem: ILineItem, optionId: TOptionId): TMinMaxStatus | null {
		const minMaxChoicesSet = this.optionFindInMinMax(optionId, lineItem);

		if(!minMaxChoicesSet) {
			return null;
		}

		const minMaxChoiceOptions = this.lineItemGetOptionsById(lineItem, minMaxChoicesSet.includedOptions);
		const count = this.optionsCountSelected(minMaxChoiceOptions, minMaxChoicesSet.countIncludedChoices);

		return {
			count,
			max: minMaxChoicesSet.max,
			min: minMaxChoicesSet.min,
			name: minMaxChoicesSet.name,
		};
	}

	/**
	 * splices the given option into `item.options`
	 *
	 * @param lineItem
	 * @param option
	 */
	lineItemPatchWithOption(lineItem: ILineItem, option: ILineItemOption): ILineItem {
		const optionIndex = lineItem.options.findIndex(opt => opt.optionId === option.optionId);
		if(optionIndex === -1) {
			console.error(`Unable to find item option matching ID ${option.optionId} in ${lineItem.name}.`);
			throw new Error('Unable to find item option to update.');
		}

		return {
			...lineItem,
			options: [
				...lineItem.options.slice(0, optionIndex),
				option,
				...lineItem.options.slice(optionIndex + 1),
			],
		};
	}

	/**
	 * if the line item is contained, returns the container option choice
	 *
	 * In contrast to lineItemGetContainerChoice, this method looks for a
	 * container option in the given line item and returns the selected choice.
	 * This looks for the selected choice.
	 *
	 * @param lineItem
	 */
	lineItemGetSelectedContainerChoice(lineItem: ILineItem): ILineItemOptionChoice | null {
		if(lineItem.hasContainer) {
			const containerOption = lineItem.options.find(opt => opt.isContainerOption);
			if(containerOption) {
				return this.optionGetSelectedChoice(containerOption);
			}
		}
		return null;
	}

	/**
	 * adds given options to line item, but only if valid
	 *
	 * Clones baseLineItem and updates its options based on givenOptions. If any of the
	 * given options' choices are determined to be invalid, this method throws an
	 * ItemValidationError error.
	 *
	 * If `forceMerge` is specified as true in options, this method will not throw
	 * an ItemValidationError under most conditions and will "silently" continue the
	 * merge without providing feedback or warning to the user.
	 *
	 * @param baseLineItem
	 * @param givenOptions
	 * @param options
	 * @return ILineItem baseLineItem updated with givenOptions
	 * @throws ItemValidationError
	 */
	lineItemValidateOptions(baseLineItem: ILineItem, givenOptions: ILineItemOption[], options: ILineItemValidateOptionsParams = {}): ILineItem {
		if(!baseLineItem || !Array.isArray(givenOptions)) {
			throw new ItemValidationError('Invalid parameter(s).');
		}

		const {
			forceMerge = false,
		} = options;

		const workingLineItem: ILineItem = cloneDeep(baseLineItem);

		/*
		 * We need to keep track of the options we're able to match and those we are not.
		 * Do so by generating a list of IDs for options that contain choices with user
		 * selections. As we process those same options on baseLineItem, remove them from
		 * the set. At the end, look for any remaining options.
		 */
		const userSelectedOptionIds: Set<TOptionId> = new Set(this.optionsOnlyUserSelections(givenOptions).map(opt => opt.optionId));

		/*
		 * Validate the givenOptions against the line item's options by iterating through
		 * all options available on workingLineItem (a clone of the baseLineItem input)
		 * and attempting to update them to match givenOptions. If one of the given options
		 * isn't found on the line item, or is no longer a valid option, short-circuit
		 * with an ItemValidationError.
		 */
		workingLineItem.options.forEach(availableOption => {
			/*
			 * Determine if this line item option (availableOption) is included in the
			 * givenOptions. If so, store a clone of the matching, given option.
			 */
			const matchedGivenOption = cloneDeep(givenOptions.find(
				selOpt => selOpt.optionId === availableOption.optionId
			));

			if(matchedGivenOption) {
				userSelectedOptionIds.delete(matchedGivenOption.optionId);

				let exclusiveChoiceId: ILineItemOptionChoice['choiceId'] | null = null;

				/*
				 * To make this method a little more efficient, we can remove all choices
				 * from matchedGivenOption (a copy from givenOptions) that are not user
				 * selections. This will most likely make the array searched below for
				 * selectedChoiceIndex smaller and, therefore, quicker to search.
				 *
				 * We only care about checking isUserSet, we don't want to use
				 * this.choiceIsUserSet since these choices are not always fully hydrated
				 * (like a favorite) and only have the bare minimum saved on them (things
				 * like mutableQuantity aren't set)
				 */
				const selectedUserChoices = matchedGivenOption.choices.filter(chc => chc.isUserSet);

				/*
				 * At this point, we know availableOption is in givenOptions (matching
				 * matchedGivenOption by value), and that availableOption and matchedGivenOption
				 * refer to the same ILineItemOption object (again by value, but not by reference).
				 *
				 * Update the choices in the availableOption (and, by reference, workingLineItem)
				 * to match those in the selectedUserChoices.
				 */
				availableOption.choices = availableOption.choices.map(availableChoice => {
					// look for the current, availableChoice in the selectedUserChoices
					const selectedChoiceIndex = selectedUserChoices.findIndex(chc => chc.choiceId === availableChoice.choiceId);

					if(selectedChoiceIndex !== -1) {
						// We've found the choice within selectedUserChoices (and matchedGivenOption and availableOption). Create an alias.
						let selectedChoice = selectedUserChoices[selectedChoiceIndex];

						// capture the exclusive option choice ID, if present
						if(availableOption.exclusive && selectedChoice.selected) {
							exclusiveChoiceId = selectedChoice.choiceId;
						}

						// The selected choice has since been excluded, making this selection invalid.
						if(availableChoice.excluded && this.choiceIsSelected(selectedChoice)) {
							if(forceMerge) {
								console.debug(`Option choice ${selectedChoice?.name ?? selectedChoice?.choiceId} now excluded.`);
								// ignore selectedChoice when constructing newChoice
								selectedChoice = availableChoice;
							} else {
								throw new ItemValidationError(`Option choice ${selectedChoice?.name ?? selectedChoice?.choiceId} now excluded.`);
							}
						}

						/*
						 * selectedChoice in matchedGivenOption remains valid as availableOption/availableChoice.
						 * Create a new choice object based on availableChoice but with some selectedChoice
						 * updates.
						 */
						const newChoice: ILineItemOptionChoice = {
							...availableChoice,
							fractionalQuantity: selectedChoice.fractionalQuantity,
							quantity: selectedChoice.quantity,
							selected: selectedChoice.selected
						};

						/*
						 * isUserSet may have changed without the choice changing,
						 * based on the underlying line item. For example, an item
						 * that has a preset size choice will show both the preset
						 * and user-set choices as isUserSet. If migrating to a line
						 * item without the preset, the unselected choice corresponding
						 * to the preset is no longer user-set.
						 */
						newChoice.isUserSet = this.choiceIsUserSet(newChoice);

						/*
						 * Remove the selected choice from selectedUserChoices. We'll use
						 * this accounting below to determine if selectedUserChoices (and,
						 * by association, matchedGivenOption) has any choices not seen in
						 * availableOption.choices.
						 */
						selectedUserChoices.splice(selectedChoiceIndex, 1);

						// return the new choice object as a replacement for the current availableOption.choices
						return newChoice;
					} else {
						// available choice not found in selection, leave as-is
						return availableChoice;
					}
				});

				/*
				 * Updating the availableOption.choices with the selected choices is complete. While
				 * mapping, we pruned every selectedUserChoices seen in availableOption.choices.
				 * However, there may be option choices that have since been removed. We can't blindly
				 * fail here if the count is greater than zero. We must make sure any lingering
				 * choices were made by the user.
				 */
				if(selectedUserChoices.length > 0) {
					if(forceMerge) {
						console.debug(`${selectedUserChoices.length} user option choice(s) remain unmatched.`, selectedUserChoices);
					} else {
						console.error(`${selectedUserChoices.length} user option choice(s) remain unmatched.`, selectedUserChoices);
						throw new ItemValidationError(`${selectedUserChoices.length} user option choice(s) remain unmatched.`);
					}
				}

				/*
				 * If there is a default selection and the option is exclusive (determined while
				 * mapping, above), we need to unselect the other choices.
				 */
				if(exclusiveChoiceId !== null) {
					availableOption.choices = availableOption.choices.map(chc => {
						if(chc.choiceId !== exclusiveChoiceId) {
							chc = this.choiceUnset(chc);
						}
						return chc;
					});
				}
			}
			// else: Option not found in givenOptions. Leave this menu option as-is in the line item.
		});

		/*
		 * If after processing all the options in baseLineItem (as workingLineItem) we still
		 * have unmatched options from givenOptions, we must throw an error.
		 */
		if(userSelectedOptionIds.size > 0) {
			if(forceMerge) {
				console.debug(`${userSelectedOptionIds.size} user option(s) remain unmatched.`, userSelectedOptionIds);
			} else {
				console.error(`${userSelectedOptionIds.size} user option(s) remain unmatched.`, userSelectedOptionIds);
				throw new ItemValidationError(`${userSelectedOptionIds.size} user option(s) remain unmatched.`);
			}
		}

		return workingLineItem;
	}

	lineItemHasHiddenProp(lineItem: ILineItem) {
		if(lineItem.hideInApp) {
			return true;
		} else {
			for(const option of lineItem.options) {
				if(option.hideInApp) {
					return true;
				} else {
					for(const choice of option.choices) {
						if(choice.hideInApp) {
							return true;
						}
					}
				}
			}
		}
		return false;
	}

	lineItemsForLocalStorage = (lineItems: ILineItem[]): ILineItem[] => {
		return lineItems.map(
			(lineItem: ILineItem) => {
				return {
					...lineItem,
					options: lineItem.options.map(
						(option) => {
							return {
								...option,
								choices: option.choices.filter(
									(choice) => {
										return this.choiceIsUserSet(choice);
									}
								)
							};
						}
					).filter(
						(option) => {
							return option.choices.length;
						}
					)
				};
			}
		);
	};

	lineItemEngagementSourceFromCustomerItemFavorite(favorite: IItemFavorite, engagementLocation: TEngagementSourceLocation): IEngagementSource {
		const menuItem = MenuStore.item(favorite.itemId, favorite.itemHqId);

		return {
			type: ENGAGEMENT_SOURCE.FAVORITE,
			location: engagementLocation,
			name: favorite.name,
			id: favorite.id,
			assetId: favorite.itemId,
			parentAssetId: favorite.itemHqId || null,
			...this.containerOptionIdOptionChoiceIdFromLineItemOptions(menuItem, favorite.options)
		};
	}

	lineItemEngagementSourceFromTrendingItem(trendingItem: ITrendingItem, engagementLocation: TEngagementSourceLocation): IEngagementSource {
		const menuItem = MenuStore.item(trendingItem.itemId);

		return {
			type: ENGAGEMENT_SOURCE.TRENDING,
			location: engagementLocation,
			name: menuItem?.name ?? 'Unknown',
			id: trendingItem.itemId,
			assetId: trendingItem.itemId,
			parentAssetId: null,
			containerOptionId: null,
			containerOptionChoiceId: null
		};
	}

	lineItemEngagementSourceFromTrendingItemWithOptions(trendingItem: ITrendingItemWithOptions, engagementLocation: TEngagementSourceLocation): IEngagementSource {
		const menuItem = MenuStore.item(trendingItem.itemId, trendingItem.itemHqId);

		return {
			type: ENGAGEMENT_SOURCE.TRENDING,
			location: engagementLocation,
			name: menuItem?.name ?? 'Unknown',
			id: trendingItem.itemHqId ?? trendingItem.itemId,
			assetId: trendingItem.itemId,
			parentAssetId: trendingItem.itemHqId,
			...this.containerOptionIdOptionChoiceIdFromLineItemOptions(menuItem, trendingItem.options)
		};
	}

	lineItemEngagementSourceFromFeaturedItem(featuredItem: IFeaturedItem, engagementLocation: TEngagementSourceLocation): IEngagementSource {
		const menuItem = MenuStore.item(featuredItem.itemId, featuredItem.itemHqId);

		return {
			type: ENGAGEMENT_SOURCE.FEATURED,
			location: engagementLocation,
			name: featuredItem.name ?? menuItem?.name ?? 'Unknown',
			id: featuredItem.id,
			assetId: menuItem?.itemId ?? '',
			parentAssetId: menuItem?.itemHqId ?? null,
			...this.containerOptionIdOptionChoiceIdFromLineItemOptions(menuItem, featuredItem.options)
		};
	}

	containerOptionIdOptionChoiceIdFromLineItemOptions(menuItem: IMenuItem | null, options: ILineItemOption[] | null): {containerOptionId: string | null; containerOptionChoiceId: string | null} {
		let containerOptionId = null;
		let containerOptionChoiceId = null;

		if(menuItem && menuItem.lineItem?.hasContainer) {
			const menuItemContainerOption = menuItem.lineItem.options.find(opt => opt.isContainerOption);
			if(menuItemContainerOption) {
				const containerOption = options !== null ? options.find(opt => opt.optionId === menuItemContainerOption.optionId) : null;
				if(containerOption) {
					containerOptionId = containerOption.optionId;
					const selectedContainerChoice = this.optionGetSelectedChoice(containerOption);
					containerOptionChoiceId = selectedContainerChoice ? selectedContainerChoice.choiceId : null;
				}
			}
		}

		return {
			containerOptionId: containerOptionId,
			containerOptionChoiceId: containerOptionChoiceId
		};
	}

	lineItemEngagementSourceFromRecentItem(recentItem: IRecentItem, engagementLocation: TEngagementSourceLocation): IEngagementSource | null {
		if(recentItem.engagementSource !== null) {
			return {
				...recentItem.engagementSource,
				location: engagementLocation
			};
		}
		return null;
	}

	lineItemEngagementSourceFromMenuItem(id: string, menuItem: IMenuItem, engagementSource: TEngagementSource, engagementLocation: TEngagementSourceLocation): IEngagementSource {
		return {
			type: engagementSource,
			location: engagementLocation,
			name: DisplayUtils.getLineItemDisplayName(menuItem.lineItem),
			id: id,
			assetId: menuItem.itemId,
			parentAssetId: menuItem.itemHqId,
			containerOptionId: null,
			containerOptionChoiceId: null
		};
	}

	isEngagementSource(engagementSource: any): engagementSource is IEngagementSource {
		return engagementSource !== null &&
			typeof engagementSource === 'object' &&
			typeof engagementSource.type !== 'undefined' &&
			typeof engagementSource.location !== 'undefined' &&
			typeof engagementSource.name !== 'undefined' &&
			typeof engagementSource.id !== 'undefined' &&
			typeof engagementSource.assetId !== 'undefined' &&
			typeof engagementSource.parentAssetId !== 'undefined' &&
			typeof engagementSource.containerOptionId !== 'undefined' &&
			typeof engagementSource.containerOptionChoiceId !== 'undefined';
	}

	engagementSourceIsFavorite(engagementSource: IEngagementSource | null): engagementSource is IEngagementSource & {type: 'favorite'} {
		return this.isEngagementSource(engagementSource) && engagementSource.type === ENGAGEMENT_SOURCE.FAVORITE;
	}

	engagementSourceIsFeatured(engagementSource: IEngagementSource | null): engagementSource is IEngagementSource & {type: 'featured'} {
		return this.isEngagementSource(engagementSource) && engagementSource.type === ENGAGEMENT_SOURCE.FEATURED;
	}

	engagementSourceIsTrending(engagementSource: IEngagementSource | null): engagementSource is IEngagementSource & {type: 'trending'} {
		return this.isEngagementSource(engagementSource) && engagementSource.type === ENGAGEMENT_SOURCE.TRENDING;
	}

	inflateLineItemsFromLocalStorage(lineItems: ILineItem[]): ILineItem[] {
		return lineItems.map(
			(lineItem) => {
				try {
					const fullLineItem = this.lineItemGetById({
						itemId: lineItem.itemId,
						itemHqId: lineItem.itemHqId,
						itemOptions: lineItem.options
					});
					if(fullLineItem === null) {
						throw new Error('No line item found');
					}
					return {
						...lineItem,
						options: fullLineItem.options
					};
				} catch(e) {
					console.warn('Failed to inflate lineItem: ', e);
					return null;
				}
			}
		).filter(
			(lineItem): lineItem is ILineItem => {
				return lineItem !== null;
			}
		);
	}

	//
	//
	// OPTION METHODS
	//

	/**
	 * if the given option is part of a min/max choice set, indicates if below maximum
	 *
	 * This returns true if the option's min/max choice set count is less than (*not* LTE)
	 * any specified maximum. If no max is specified, this method returns true.
	 *
	 * TODO?: consider option.maxChoices
	 *
	 * @param optionId
	 * @param lineItem
	 */
	optionCanAddChoice(optionId: string, lineItem: ILineItem): boolean {
		const minMax = this.lineItemMinMaxStatusForOption(lineItem, optionId);

		if(minMax === null) {
			return true;
		}

		return minMax.count < minMax.max;
	}

	/**
	 * returns a copy of the min/max choices set from lineItem containing the given option ID, or null if not found
	 *
	 * This method assumes an option should appear in only one minMaxChoicesSet.
	 *
	 * @param optionId
	 * @param lineItem
	 */
	optionFindInMinMax(optionId: string, lineItem: ILineItem): IMinMaxChoicesSet | null {
		if(lineItem.minMaxChoicesSets.length === 0) {
			return null;
		}

		return lineItem.minMaxChoicesSets.find(
			mmcs => mmcs.includedOptions.some(
				optId => optId === optionId
			)
		) || null;
	}

	/**
	 * Only use this for container options (or potentially exclusive options)
	 */
	optionGetSelectedChoice(option: ILineItemOption): ILineItemOptionChoice | null {
		return option?.choices?.find(chc => this.choiceIsSelected(chc)) ?? null;
	}

	optionIsExclusive(option: ILineItemOption): boolean {
		return option.exclusive || option.maxChoices === 1;
	}

	/**
	 * indicates if the given option ID appears in a min/max choices set on lineItem
	 *
	 * This method assumes an option should appear in only one minMaxChoicesSet.
	 *
	 * @param optionId
	 * @param lineItem
	 */
	optionIsInMinMax(optionId: string, lineItem: ILineItem): boolean {
		return this.optionFindInMinMax(optionId, lineItem) !== null;
	}

	optionIsRequired(option: ILineItemOption, lineItem: ILineItem | null = null): boolean {
		if(option.mobileInline || option.isContainerOption || option.required) {
			return true;
		}

		// There are some cases where we don't care about the minimum
		// in those cases, we can pass in null for lineItem
		if(lineItem) {
			return this.optionRequiresMinimum(option, lineItem);
		}
		return false;
	}

	/**
	 * validates that the option meets all its internal requirements
	 *
	 * @param option
	 */
	optionIsValid(option: ILineItemOption): boolean {
		// If option is required make there is a selected choice
		if(option.required && this.optionIsExclusive(option)) {
			let optionHasSelectedChoice = false;
			for(const choice of option.choices) {
				optionHasSelectedChoice = this.choiceIsSelected(choice);
				if(optionHasSelectedChoice) {
					// Once we find a selection we can stop looking
					break;
				}
			}
			if(!optionHasSelectedChoice) {
				return false;
			}
		}

		return true;
	}

	/**
	 * updates `option.choices` with the given choice
	 *
	 * If the option is exclusive, all other choices are deselected.
	 * This method updates the choices' `isUserSet` properties.
	 *
	 * @param option
	 * @param choice
	 */
	optionPatchWithChoice(option: ILineItemOption, choice: ILineItemOptionChoice): ILineItemOption {
		const choiceIndex = option.choices.findIndex(chc => chc.choiceId === choice.choiceId);
		if(choiceIndex === -1) {
			console.error(`Unable to find item option choice matching ID ${choice.choiceId} in ${option.name}.`);
			throw new Error('Unable to find item option choice to update.');
		}

		let updatedOption: ILineItemOption;

		if(this.optionIsExclusive(option)) {
			// Deselect any other selected choices and select the new one.
			updatedOption = {
				...option,
				choices: [
					...option.choices.slice(0, choiceIndex).map(chc => this.choiceUnset(chc)),
					choice,
					...option.choices.slice(choiceIndex + 1).map(chc => this.choiceUnset(chc)),
				].map(chc => this.choiceUpdateIsUserSet(chc)),
			};
		} else {
			updatedOption = {
				...option,
				choices: [
					...option.choices.slice(0, choiceIndex),
					choice,
					...option.choices.slice(choiceIndex + 1)
				].map(chc => this.choiceUpdateIsUserSet(chc)),
			};
		}

		return updatedOption;
	}

	/**
	 * indicates if the given option is part of a min/max choice set with a non-zero minimum
	 *
	 * @param option
	 * @param lineItem
	 */
	optionRequiresMinimum(option: ILineItemOption, lineItem: ILineItem): boolean {
		const minMax = this.lineItemMinMaxStatusForOption(lineItem, option.optionId);
		return minMax !== null && minMax.min > 0;
	}


	//
	//
	// OPTIONS METHODS
	//

	/**
	 * returns a count of set options
	 *
	 * Quantity is included in the count. Otherwise, fractional quantities and
	 * "selected" choices each count as 1.
	 */
	optionsCountSelected(options: ILineItemOption[], countIncludedChoices: boolean = false) {
		return options.reduce((sum, option) => {
			return option.choices.reduce((innerSum, choice) => {
				if(this.choiceIsSelected(choice)) {
					if(choice.quantity > 0) {
						// If we have a quantity use that
						if(countIncludedChoices) {
							return innerSum + choice.quantity;
						} else {
							return innerSum + Math.max(0, choice.quantity - choice.includedQuantity);
						}
					} else {
						// Treat selected and any mutable quantity as 1
						if(countIncludedChoices || !choice.included) {
							return innerSum + 1;
						} else {
							return innerSum;
						}
					}
				}
				return innerSum;
			}, sum);
		}, 0);
	}

	/**
	 * filters given options list to only those with choices containing selections
	 *
	 * @param options
	 */
	optionsWithSelections(options: ILineItemOption[]): ILineItemOption[] {
		const workingOptions = cloneDeep(options);

		return workingOptions.map(opt => {
			opt.choices = opt.choices.filter(chc => {
				return this.choiceIsSelected(chc) || this.choiceIsUserSet(chc);
			}) ?? [];
			return opt;
		}).filter(opt => opt.choices.length > 0);
	}

	/**
	 * filters given options list to only those with user set choices (aka, sparse options)
	 *
	 * @param options
	 */
	optionsOnlyUserSelections(options: ILineItemOption[]): ILineItemOption[] {
		const workingOptions = cloneDeep(options);

		return workingOptions.map(opt => {
			opt.choices = opt.choices.filter(chc => this.choiceIsUserSet(chc)) ?? [];
			return opt;
		}).filter(opt => opt.choices.length > 0);
	}


	//
	//
	// CHOICE METHODS
	//

	/**
	 * returns true if the option is included and the selected choice quantity is non-default
	 * 
	 * @param choice
	 */
	isQuantityDefault(choice: ILineItemOptionChoice) {
		// Determine if option is included
		if(choice.included) {
			// Determine if current quantity is the default quantity
			return choice.includedQuantity === choice.quantity;
		}
		return true;
	}

	/**
	 * selects or increases quantity of choice
	 *
	 * N.B.: This returns only the updated fields (a patch) for use with choicePatch.
	 *
	 * TODO: compare fractional logic to generic/.../ItemViewOptionChoice.vue's handleInput
	 *
	 * @param choice
	 */
	choiceAdd(choice: ILineItemOptionChoice): Partial<ILineItemOptionChoice> {
		const updatedChoice: Partial<ILineItemOptionChoice> = {
			isUserSet: true,
		};

		if(choice.mutableQuantity) {
			if(choice.fractionalQuantity > 0) {
				// If we are at 1/2 and we click "+" we want to go to 1 quantity
				if(choice.fractionalQuantity === choice.maxFractionalQuantity - 1) {
					updatedChoice.quantity = 1;
					updatedChoice.fractionalQuantity = 0;
				} else {
					updatedChoice.fractionalQuantity = choice.fractionalQuantity + 1;
				}
			} else {
				updatedChoice.quantity = choice.quantity + 1;
			}
		} else {
			updatedChoice.selected = true;
		}

		return updatedChoice;
	}

	/* private */ greatestCommonDivisor(a: number, b: number): number {
		while(b !== 0) {
			const t = b;
			b = a % b;
			a = t;
		}
		return a;
	}

	choiceGetQuantityDisplayValue(choice: ILineItemOptionChoice): string {
		if(choice.fractionalQuantity > 0) {
			const gcd = this.greatestCommonDivisor(choice.fractionalQuantity, choice.maxFractionalQuantity);
			return `${(choice.fractionalQuantity / gcd)}/${(choice.maxFractionalQuantity / gcd)}`;
		} else {
			return `${choice.quantity}`;
		}
	}

	/**
	 * returns the choice image
	 *
	 * This method assumes use with CachedImage, and therefore DOES NOT return a
	 * value processed with WebService.assetPath.
	 *
	 * @param choice
	 */
	choiceGetImagePath(choice: ILineItemOptionChoice): string | null {
		if(choice?.containerImage) {
			return choice.containerImage;
		}
		return choice?.image ?? '';
	}

	choiceHasQuantity(choice: ILineItemOptionChoice, countIncluded = false): boolean {
		const rv = choice.quantity > 0 || choice.fractionalQuantity > 0;

		if(countIncluded) {
			return rv || choice.includedQuantity > 0;
		}

		return rv;
	}

	choiceIsDecrementDisabled(choice: ILineItemOptionChoice): boolean {
		// If we are into fractional quantities, the denominator can't be more than the max
		if(choice.fractionalQuantity > 0) {
			return choice.fractionalQuantity <= 0;
		} else {
			if(choice.maxFractionalQuantity === 0) {
				// If we don't have fractional quantities, we can't go lower than 0
				return choice.quantity <= 0;
			}
			return false;
		}
	}

	choiceIsIncluded(choice: ILineItemOptionChoice): boolean {
		return choice.included || choice.includedQuantity > 0;
	}

	choiceIsIncrementDisabled(choice: ILineItemOptionChoice): boolean {
		if(choice.fractionalQuantity > 0) {
			// Only disable if we can't go higher than 1
			if(choice.maxQuantity <= 1) {
				return choice.fractionalQuantity >= choice.maxFractionalQuantity;
			}
			return false;
		} else {
			return choice.quantity >= choice.maxQuantity;
		}
	}

	choiceIsSelected(choice: ILineItemOptionChoice): boolean {
		return choice.selected || this.choiceHasQuantity(choice, false);
	}

	choiceIsUserSet(choice: ILineItemOptionChoice): boolean {
		// short-circuit if known isUserSet
		// short-circuiting the short-circuit so that, if the choice status
		// is reverted to default, we don't consider it set by the user.
		// if(choice.isUserSet) {
		// 	// console.log(`[choiceIsUserSet] ${choice.name} isUserSet`);
		// 	return true;
		// }

		/*
		 * If the choice has a mutable quantity, first look to see if the
		 * quantity doesn't match the includedQuantity, meaning the user
		 * has modified it from its initial value. Fractional quantities
		 * do not have an "included" counterpart, so any non-zero value
		 * also indicates a user-made change.
		 *
		 * If there is no mutable quantity, we must look at selection.
		 * Only if mutableQuantity is false can we compare included and
		 * selected. (Otherwise, when includedQuantity > 0, included is
		 * updated to be true but selected is not updated.)
		 */
		if(choice.mutableQuantity) {
			return choice.quantity !== choice.includedQuantity || choice.fractionalQuantity > 0;
		} else {
			return choice.selected !== choice.included;
		}
	}

	/**
	 * patches the given `ILineItemOptionChoice` with updates
	 *
	 * This method updates the choice's `isUserSet` property.
	 *
	 * @param base
	 * @param updates
	 */
	choicePatch(base: ILineItemOptionChoice, updates: Partial<ILineItemOptionChoice>): ILineItemOptionChoice {
		const updatedChoice: ILineItemOptionChoice = {
			...base,
			...updates,
		};
		updatedChoice.isUserSet = this.choiceIsUserSet(updatedChoice);

		return updatedChoice;
	}

	/**
	 * deselects or reduces quantity of choice
	 *
	 * N.B.: This returns only the updated fields (a patch) for use with choicePatch.
	 *
	 * @param choice
	 */
	choiceSubtract(choice: ILineItemOptionChoice): Partial<ILineItemOptionChoice> {
		const updatedChoice: Partial<ILineItemOptionChoice> = {
			isUserSet: true,
		};
		if(choice.mutableQuantity) {
			if(choice.fractionalQuantity > 0) {
				updatedChoice.fractionalQuantity = choice.fractionalQuantity - 1;
			} else {
				if(choice.quantity > 1) {
					updatedChoice.quantity = choice.quantity - 1;
				} else {
					updatedChoice.quantity = 0;
					if(choice.maxFractionalQuantity > 0) {
						updatedChoice.fractionalQuantity = choice.maxFractionalQuantity - 1;
					} else {
						updatedChoice.selected = false;
					}
				}
			}
		} else {
			updatedChoice.selected = false;
		}

		return updatedChoice;
	}

	choiceUnset(choice: ILineItemOptionChoice): ILineItemOptionChoice {
		return {
			...choice,
			fractionalQuantity: 0,
			isUserSet: false,
			quantity: 0,
			selected: false,
		};
	}

	choiceUpdateIsUserSet(choice: ILineItemOptionChoice): ILineItemOptionChoice {
		return {
			...choice,
			isUserSet: this.choiceIsUserSet(choice),
		};
	}


	//
	//
	// CHOICES METHODS
	//

	/**
	 * This is used in the ItemOptionsSidebar to show choices that are being applied to the item.
	 */
	choiceGetActive(choices: ILineItemOptionChoice[], allowHiddenChoiceIds: string[] = []): ILineItemOptionChoice[] {
		return choices.filter(
			(chc) => {
				return !chc.excluded &&
				(
					(
						chc.hideInApp &&
						(
							this.choiceIsSelected(chc) ||
							this.choiceHasQuantity(chc) ||
							allowHiddenChoiceIds.includes(chc.choiceId)
						)
					) ||
					(
						!chc.hideInApp &&
						(
							this.choiceIsSelected(chc) ||
							this.choiceHasQuantity(chc) || 
							this.choiceIsUserSet(chc)
						)
					)
				);
			}
		);
	}

	/**
	 * This is used in the ItemOptionChoiceSidebar for all the editable choices.
	 */
	choiceGetAvailable(choices: ILineItemOptionChoice[], allowHiddenChoiceIds: string[] = []): ILineItemOptionChoice[] {
		return choices.filter(
			(chc) => {
				return !chc.excluded &&
					(
						!chc.hideInApp ||
						(
							chc.hideInApp &&
							(
								this.choiceIsSelected(chc) ||
								this.choiceHasQuantity(chc) ||
								allowHiddenChoiceIds.includes(chc.choiceId)
							)
						)
					);
			}
		);
	}
}

export const ItemService = new Item();
